import React, { useState } from 'react'
import { X, Plus, Trash2, CheckCircle2, AlertCircle } from 'lucide-react'
import { PaymentMethod, PaymentSplit, DEFAULT_QUICK_AMOUNTS } from '@/types/pos'

interface SplitPaymentModalProps {
  total: number
  onComplete: (splits: PaymentSplit[], amountPaid: number, change: number) => void
  onClose: () => void
}

export const SplitPaymentModal: React.FC<SplitPaymentModalProps> = ({
  total,
  onComplete,
  onClose,
}) => {
  const [splits, setSplits] = useState<PaymentSplit[]>([
    { method: 'cash', amount: 0 },
    { method: 'card', amount: 0 },
  ])
  const [activeIndex, setActiveIndex] = useState<number>(0)

  const paymentMethods: { id: Exclude<PaymentMethod, 'split'>; label: string; icon: string }[] = [
    { id: 'cash', label: 'Cash', icon: '💵' },
    { id: 'card', label: 'Card', icon: '💳' },
    { id: 'wallet', label: 'E-Wallet', icon: '📱' },
    { id: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
  ]

  const totalPaid = splits.reduce((sum, split) => sum + split.amount, 0)
  const remaining = Math.max(0, total - totalPaid)
  const change = Math.max(0, totalPaid - total)
  const isComplete = totalPaid >= total

  const updateSplitAmount = (index: number, amount: number) => {
    const newSplits = [...splits]
    newSplits[index] = { ...newSplits[index], amount: Math.max(0, amount) }
    setSplits(newSplits)
  }

  const updateSplitMethod = (index: number, method: Exclude<PaymentMethod, 'split'>) => {
    // Prevent duplicate payment methods — check if already used in another split
    const existingIndex = splits.findIndex((s, i) => i !== index && s.method === method)
    if (existingIndex >= 0) return // Silently reject — dropdown should filter these out
    const newSplits = [...splits]
    newSplits[index] = { ...newSplits[index], method }
    setSplits(newSplits)
  }

  const addSplit = () => {
    // Find an unused payment method
    const usedMethods = splits.map(s => s.method)
    const availableMethod = paymentMethods.find(m => !usedMethods.includes(m.id))
    
    setSplits([...splits, { 
      method: availableMethod?.id || 'cash', 
      amount: 0 
    }])
    setActiveIndex(splits.length)
  }

  const removeSplit = (index: number) => {
    if (splits.length > 1) {
      const newSplits = splits.filter((_, i) => i !== index)
      setSplits(newSplits)
      if (activeIndex >= newSplits.length) {
        setActiveIndex(newSplits.length - 1)
      }
    }
  }

  const handleQuickAmount = (amount: number) => {
    if (activeIndex >= 0 && activeIndex < splits.length) {
      updateSplitAmount(activeIndex, splits[activeIndex].amount + amount)
    }
  }

  const fillRemaining = () => {
    if (activeIndex >= 0 && activeIndex < splits.length && remaining > 0) {
      updateSplitAmount(activeIndex, splits[activeIndex].amount + remaining)
    }
  }

  const handleComplete = () => {
    if (isComplete) {
      // Filter out zero-amount splits
      const validSplits = splits.filter(s => s.amount > 0)
      onComplete(validSplits, totalPaid, change)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-bold esales-text">Split Payment</h3>
            <p className="text-xs text-muted-foreground mt-1">Divide payment across multiple methods</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Total Summary */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="p-3 bg-muted/20 rounded-xl text-center">
            <p className="text-xs text-muted-foreground">Total Due</p>
            <p className="text-xl font-bold text-foreground">Rs.{total.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-primary/10 rounded-xl text-center">
            <p className="text-xs text-muted-foreground">Total Paid</p>
            <p className="text-xl font-bold text-primary">Rs.{totalPaid.toFixed(2)}</p>
          </div>
          <div className={`p-3 rounded-xl text-center ${remaining > 0 ? 'bg-yellow-500/10' : 'bg-green-500/10'}`}>
            <p className="text-xs text-muted-foreground">{remaining > 0 ? 'Remaining' : 'Change'}</p>
            <p className={`text-xl font-bold ${remaining > 0 ? 'text-yellow-500' : 'text-green-500'}`}>
              Rs.{(remaining > 0 ? remaining : change).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Payment Splits */}
        <div className="space-y-3 mb-6">
          {splits.map((split, index) => (
            <div
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                activeIndex === index
                  ? 'border-primary bg-primary/5'
                  : 'border-border/50 bg-muted/10'
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Payment Method Selector */}
                <select
                  value={split.method}
                  onChange={(e) => updateSplitMethod(index, e.target.value as any)}
                  onClick={(e) => e.stopPropagation()}
                  className="px-3 py-2 bg-background border border-border rounded-lg text-sm font-semibold"
                >
                  {paymentMethods
                    .filter(m => m.id === split.method || !splits.some((s, i) => i !== index && s.method === m.id))
                    .map(m => (
                    <option key={m.id} value={m.id}>{m.icon} {m.label}</option>
                  ))}
                </select>

                {/* Amount Input */}
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">Rs.</span>
                  <input
                    type="number"
                    value={split.amount || ''}
                    onChange={(e) => updateSplitAmount(index, parseFloat(e.target.value) || 0)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={() => setActiveIndex(index)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-4 py-2 bg-background border border-border rounded-lg text-right font-mono text-lg"
                  />
                </div>

                {/* Remove Button */}
                {splits.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeSplit(index)
                    }}
                    className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Quick Actions for Active Split */}
              {activeIndex === index && (
                <div className="mt-3 pt-3 border-t border-border/50">
                  <div className="flex flex-wrap gap-2">
                    {DEFAULT_QUICK_AMOUNTS.map(qa => (
                      <button
                        key={qa.value}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleQuickAmount(qa.value)
                        }}
                        className="px-3 py-1 bg-muted hover:bg-muted/80 rounded-lg text-xs font-semibold transition-colors"
                      >
                        +{qa.label}
                      </button>
                    ))}
                    {remaining > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          fillRemaining()
                        }}
                        className="px-3 py-1 bg-primary/20 text-primary hover:bg-primary/30 rounded-lg text-xs font-semibold transition-colors"
                      >
                        Fill Rs.{remaining.toFixed(2)}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Another Payment */}
        {splits.length < paymentMethods.length && (
          <button
            onClick={addSplit}
            className="w-full py-3 border-2 border-dashed border-border hover:border-primary/50 rounded-xl flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-semibold">Add Another Payment Method</span>
          </button>
        )}

        {/* Status Message */}
        {!isComplete && remaining > 0 && (
          <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-4">
            <AlertCircle className="w-4 h-4 text-yellow-500" />
            <p className="text-sm text-yellow-600 dark:text-yellow-400">
              Rs.{remaining.toFixed(2)} remaining to complete payment
            </p>
          </div>
        )}

        {isComplete && (
          <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <p className="text-sm text-green-600 dark:text-green-400">
              Payment complete! {change > 0 && `Change due: Rs.${change.toFixed(2)}`}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleComplete}
            disabled={!isComplete}
            className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed glow-effect transition-colors"
          >
            Complete Payment
          </button>
        </div>
      </div>
    </div>
  )
}

export default SplitPaymentModal
